import { FilledDataset, FilledDatasetValue } from '../../lib/api';

export interface ChoiceOption {
  value: string;
  label: string;
  score?: number;
}

export interface ConditionalLogic {
  show_if?: {
    question_id: number;
    operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'is_empty' | 'is_not_empty';
    value?: string | boolean | number;
  }[];
  logic?: 'and' | 'or';
}

export interface MatrixConfigRow {
  id: string;
  label: string;
}

export interface MatrixConfigColumn {
  id: string;
  label: string;
  score?: number;
}

export interface MatrixConfig {
  rows: MatrixConfigRow[];
  columns: MatrixConfigColumn[];
  allowMultiple?: boolean;
}

export interface Question {
  id: number;
  question_text: string;
  help_text?: string;
  question_type: string;
  is_required: boolean;
  sort_order: number;
  choices?: Array<ChoiceOption | string>;
  max_score?: number;
  conditional_logic?: ConditionalLogic;
  matrix_config?: MatrixConfig;
}

export interface Section {
  id: number;
  title: string;
  description?: string;
  sort_order: number;
  questions: Question[];
  conditional_logic?: ConditionalLogic;
}

export interface DatasetDetail {
  id: number;
  name: string;
  description?: string;
  dataset_type: string;
  sections: Section[];
}

export interface FilledDatasetDetail extends FilledDataset {
  dataset_detail?: DatasetDetail;
  values?: FilledDatasetValue[];
  more_info_feedback?: string;
  rejection_reason?: string;
  reviewed_by_name?: string;
  reviewed_at?: string;
}

/** Helper to normalize choices - handles both string arrays and object arrays */
export const normalizeChoices = (choices?: Array<ChoiceOption | string>): ChoiceOption[] => {
  if (!choices) return [];
  return choices.map((choice) => {
    if (typeof choice === 'string') {
      return { value: choice, label: choice };
    }
    return choice;
  });
};

export const statusColors: Record<string, string> = {
  not_started: 'bg-gray-100 text-gray-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  submitted: 'bg-blue-100 text-blue-800',
  under_review: 'bg-purple-100 text-purple-800',
  more_info_required: 'bg-amber-100 text-amber-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

/** Answer value type used throughout the questionnaire */
export type AnswerValue = string | number | boolean | string[] | Record<string, unknown>;
